import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  gymDarkContentPadding,
  gymDarkHeaderBar,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialPageBg,
  gymSocialTabActive,
  gymSocialTabInactive,
} from '../gymPageTheme';
import GymEmailTagsPanel from './GymEmailTagsPanel';
import GymEmailCampaignsPanel from './GymEmailCampaignsPanel';
import GymEmailTemplatesPanel from './GymEmailTemplatesPanel';
import GymEmailAutomationsPanel from './GymEmailAutomationsPanel';

const TABS = ['campaigns', 'automations', 'tags', 'templates'];

export default function GymEmailPage() {
  const { t } = useTranslation();
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  const [tab, setTab] = useState('campaigns');
  const [templateDraft, setTemplateDraft] = useState(null);

  const handleApplyTemplate = useCallback((draft) => {
    setTemplateDraft(draft);
    setTab('campaigns');
  }, []);

  const clearTemplateDraft = useCallback(() => setTemplateDraft(null), []);

  return (
    <div className={`w-full max-w-full min-h-full ${gymSocialPageBg}`}>
      <header className={`${gymDarkHeaderBar} px-fluid-lg py-fluid-md sm:px-fluid-xl`}>
        <div className="flex items-center gap-3">
          <Mail className="h-8 w-8 text-white shrink-0" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white truncate">{t('gym.email.title')}</h1>
            <p className="text-sm text-white/80 mt-0.5">{t('gym.email.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={tab === key ? gymSocialTabActive : gymSocialTabInactive}
            >
              {key === 'campaigns'
                ? t('gym.email.tabs.campaigns')
                : key === 'automations'
                  ? t('gym.email.tabs.automations')
                : key === 'tags'
                  ? t('gym.email.tabs.tags')
                  : t('gym.email.tabs.templates')}
            </button>
          ))}
        </div>
      </header>

      <div className={`${gymDarkContentPadding} w-full max-w-full space-y-6`}>
        {tab === 'tags' && <GymEmailTagsPanel api={api} />}

        {tab === 'automations' && <GymEmailAutomationsPanel api={api} />}

        {tab === 'campaigns' && (
          <GymEmailCampaignsPanel
            api={api}
            templateDraft={templateDraft}
            onTemplateDraftConsumed={clearTemplateDraft}
          />
        )}
        {tab === 'templates' && (
          <GymEmailTemplatesPanel api={api} onApplyTemplate={handleApplyTemplate} />
        )}
      </div>
    </div>
  );
}
